import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  Button
} from '@mui/material';
import {
  Groups as EmployeesIcon,
  Payment as PayrollIcon,
  Schedule as TimeIcon,
  TrendingUp as PerformanceIcon,
  Assessment as AnalyticsIcon
} from '@mui/icons-material';

// Import components dynamically to avoid circular dependencies
import EmployeeManagement from './EmployeeManagement';
import HRAnalyticsDashboard from '../hr/HRAnalyticsDashboard';

const EmployeeHub: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const navigate = useNavigate();

  const TabPanel = ({ children, value, index }: any) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );

  const renderFeatureCard = (title: string, description: string, features: string[], route: string, available: boolean = false) => (
    <Card>
      <CardContent>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <AnalyticsIcon sx={{ fontSize: 80, color: available ? 'primary.main' : 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {description}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {features.map(feature => (
              <Chip key={feature} label={feature} variant="outlined" />
            ))}
          </Box>
          {available ? (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(route)}
            >
              Open {title}
            </Button>
          ) : (
            <Chip label="Coming Soon" color="warning" />
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3, pt: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4, mt: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Employee Management Hub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete workforce management - from hiring to performance tracking
        </Typography>
      </Box>

      {/* Quick Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <EmployeesIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary">
                24
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Employees
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PayrollIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="success.main">
                £45,250
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monthly Payroll
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TimeIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="info.main">
                1,840
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hours This Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PerformanceIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                4.2
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Performance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Navigation Alert */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom>
          🎯 Streamlined Navigation
        </Typography>
        <Typography variant="body2">
          All employee-related functions are now consolidated in this hub. Use the tabs below to access
          employee directory, payroll, time tracking, and performance management.
        </Typography>
      </Alert>

      {/* Main Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<EmployeesIcon />}
            label="Employee Directory"
            iconPosition="start"
          />
          <Tab
            icon={<PayrollIcon />}
            label="Payroll Processing"
            iconPosition="start"
          />
          <Tab
            icon={<TimeIcon />}
            label="Time & Attendance"
            iconPosition="start"
          />
          <Tab
            icon={<PerformanceIcon />}
            label="Performance Management"
            iconPosition="start"
          />
          <Tab
            icon={<AnalyticsIcon />}
            label="HR Analytics"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <TabPanel value={selectedTab} index={0}>
        <EmployeeManagement />
      </TabPanel>

      <TabPanel value={selectedTab} index={1}>
        {renderFeatureCard(
          "Payroll Processing",
          "UK-compliant payroll system with HMRC Making Tax Digital integration.",
          ["PAYE Calculations", "National Insurance", "Pension Contributions", "Professional Payslips", "HMRC Submissions"],
          "/payroll",
          true
        )}
      </TabPanel>

      <TabPanel value={selectedTab} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {renderFeatureCard(
              "Time Tracking",
              "Real-time time tracking with project allocation and mobile support.",
              ["Clock In/Out", "Project Time Tracking", "Overtime Calculations", "Mobile Support"],
              "/time-tracking",
              true
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderFeatureCard(
              "Time Approvals",
              "Manager approval workflows with bulk actions and reporting.",
              ["Bulk Approvals", "Department Filtering", "Overtime Alerts", "Audit Trails"],
              "/time-approval",
              true
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={selectedTab} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            {renderFeatureCard(
              "Performance Overview",
              "Complete performance management dashboard with analytics.",
              ["Performance Summary", "Goal Tracking", "Review Status"],
              "/performance",
              true
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {renderFeatureCard(
              "Goal Management",
              "Advanced goal setting and tracking with OKR framework.",
              ["SMART Goals", "Progress Tracking", "Competency Linking"],
              "/goals",
              true
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {renderFeatureCard(
              "Performance Reviews",
              "360-degree performance reviews with competency assessments.",
              ["Self Assessment", "Manager Reviews", "Development Planning"],
              "/reviews",
              true
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={selectedTab} index={4}>
        <HRAnalyticsDashboard />
      </TabPanel>
    </Box>
  );
};

export default EmployeeHub;
